/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { toUrlPath, getOwnerConfigFromEnv } from "@cosmos/hygraph-data";
import { GraphQLClient } from "graphql-request";

import {
	LanguageCode,
	SitemapArray,
	SitemapConfig,
	SitemapItem,
	SitemapItemAlternatesLanguages,
	SitemapOwnerFilter,
} from "../types";

import { getLocalizedPath } from "./locale-path.utils";
// Import from constants file - kept separate to allow Cloudflare Workers
// to import constants without pulling in @cosmos/hygraph-data
import { ownerFilterableContentTypes } from "./sitemap.constants";

// Re-export constants for backwards compatibility
export { commonSitemapContentTypes, ownerFilterableContentTypes } from "./sitemap.constants";

export interface HygraphPageContent {
	id: string;
	slug: string;
	stage: string;
	metaTags: {
		id: string;
		noIndex: boolean | null;
		localizations: { locale: string }[];
	};
	localizations: { locale: string }[];
	updatedAt: string;
}

export interface HygraphPageQuery {
	content: HygraphPageContent[];
}

/* Locale Processing*/
export const getSitemapLocale = (
	locale: string,
	locales: LanguageCode[],
	fallbackLocale: LanguageCode,
): LanguageCode => {
	const languageCode = locale.replace("_", "-") as LanguageCode;
	if (locales.includes(languageCode)) return languageCode;
	return fallbackLocale;
};

/**
 * Builds the GraphQL where clause for owner filtering.
 *
 * @param ownerFilter - Owner filter configuration
 * @returns GraphQL where clause string or empty string if no filter
 */
const buildOwnerWhereClause = (ownerFilter?: SitemapOwnerFilter): string => {
	if (!ownerFilter?.ownerId) {
		return "";
	}

	if (ownerFilter.isDefaultOwner) {
		// For default owners: include pages with no owner OR matching owner
		return `, where: { OR: [{ owner: null }, { owner: { ownerId: "${ownerFilter.ownerId}" } }] }`;
	}

	// For non-default owners: only include pages with matching owner
	return `, where: { owner: { ownerId: "${ownerFilter.ownerId}" } }`;
};

/** Page size for sitemap pagination */
const SITEMAP_PAGE_SIZE = 1000;

/**
 * Builds the query for fetching sitemap content with pagination support.
 */
const buildSitemapQuery = (contentType: string, whereClause: string, skip: number): string => {
	return `query ContentQuery {
      content: ${contentType}(first: ${SITEMAP_PAGE_SIZE}, skip: ${skip}${whereClause}) {
        slug
        id
        metaTags {
          id
          noIndex
          localizations {
            locale
          }
        }
        localizations {
          locale
        }
        updatedAt
      }
    }`;
};

/* Hygraph Data Processing */
export const getSitemapContentQuery = async (
	contentType: string,
	hygraphClient: GraphQLClient,
	appTitle: string,
	ownerFilter?: SitemapOwnerFilter,
): Promise<HygraphPageContent[]> => {
	// Only apply owner filter to content types that support it
	const supportsOwnerFilter = ownerFilterableContentTypes.has(contentType);

	// For non-default owners, skip content types that don't support owner filtering
	// These pages belong to the default owner and shouldn't appear in non-default sitemaps
	if (ownerFilter?.ownerId && !ownerFilter.isDefaultOwner && !supportsOwnerFilter) {
		console.log(
			`${appTitle} Sitemap: Skipping ${contentType} (no owner support, non-default owner: ${ownerFilter.ownerId})`,
		);
		return [];
	}

	const whereClause = supportsOwnerFilter ? buildOwnerWhereClause(ownerFilter) : "";
	const filterInfo = supportsOwnerFilter && ownerFilter?.ownerId ? ` (owner: ${ownerFilter.ownerId})` : "";

	hygraphClient.setHeader("user-agent", `${appTitle} Sitemap generator`);

	const allContent: HygraphPageContent[] = [];
	let skip = 0;
	let hasMore = true;

	try {
		// Paginate through all content to handle >1000 items
		while (hasMore) {
			const query = buildSitemapQuery(contentType, whereClause, skip);

			if (skip === 0) {
				console.log(`${appTitle} Sitemap generator hitting Hygraph for ContentQuery [${contentType}]${filterInfo}`);
			}

			const result = await hygraphClient.request<HygraphPageQuery>(query);
			const batch = result?.content ?? [];

			allContent.push(...batch);

			// If we got less than page size, we've fetched all items
			hasMore = batch.length === SITEMAP_PAGE_SIZE;
			skip += SITEMAP_PAGE_SIZE;

			// Safety limit to prevent infinite loops (max 100k items per content type)
			// If hit, this is a critical SEO issue that needs immediate attention
			if (skip >= 100000) {
				const error = `${appTitle} Sitemap: Hit 100k pagination limit for ${contentType} - SITEMAP INCOMPLETE`;
				console.error(error);
				throw new Error(error);
			}
		}

		console.log(`${appTitle} Sitemap generator got ${allContent.length} ${contentType} pages`);
		return allContent;
	} catch (e) {
		console.error(`${appTitle} Sitemap generator, getSitemapContentQuery() caught error:`, (e as Error)?.message);
		throw e;
	}
};

export const convertHygraphItem = (
	content: HygraphPageContent,
	sitemapConfig: SitemapConfig,
	pageSlugPrefix?: string | null,
): SitemapItem => {
	// Strip the prefix from Hygraph slug to get the public URL path
	let urlPath = toUrlPath(content.slug, pageSlugPrefix);

	// Replace "/home" with "/"
	if (urlPath === "/home") {
		urlPath = "/";
	}

	// Prioritize metaTags localizations due to Hygraph bug on model
	const localizations = content?.metaTags?.localizations || content?.localizations || [];

	// Get localizations except base one
	const alternateLocales = localizations
		?.map((el) => getSitemapLocale(el.locale, sitemapConfig?.alternates, sitemapConfig?.defaultLocale))
		?.filter((el) => el !== sitemapConfig?.defaultLocale);

	const localePrefix = sitemapConfig.localePrefix ?? "always";
	const defaultLocalePath = getLocalizedPath(
		urlPath,
		sitemapConfig.defaultLocale,
		sitemapConfig.defaultLocale,
		localePrefix,
	);

	return {
		url: `${sitemapConfig?.baseUrl}${defaultLocalePath}`,
		lastModified: new Date(content.updatedAt),
		changeFrequency: "daily",
		priority: urlPath === "/" ? 1 : 0.8,
		alternates: {
			languages: alternateLocales?.reduce((acc, locale) => {
				const localePath = getLocalizedPath(urlPath, locale, sitemapConfig.defaultLocale, localePrefix);
				acc[locale] = `${sitemapConfig?.baseUrl}${localePath}`;
				return acc;
			}, {} as SitemapItemAlternatesLanguages),
		},
	};
};

/* SitemapItem filtering */
export const filterSitemapSlug = (slug?: string): boolean => typeof slug === "string" && slug?.startsWith("/");
export const filterOutNoIndexSitemapItems = (content: HygraphPageContent): boolean =>
	content?.metaTags?.noIndex !== true;
export const filterOutNoSlashSitemapItems = (content: HygraphPageContent): boolean => filterSitemapSlug(content?.slug);

/* Process HygraphItems */
export const processSitemapHygraphItems = async (sitemapConfig: SitemapConfig): Promise<SitemapItem[]> => {
	// Automatically detect and build owner filter from environment
	let ownerFilter = sitemapConfig.ownerFilter;

	if (!ownerFilter) {
		// Use shared getOwnerConfigFromEnv which leverages cached owner data
		const ownerConfig = await getOwnerConfigFromEnv();
		if (ownerConfig.ownerId) {
			ownerFilter = {
				ownerId: ownerConfig.ownerId,
				isDefaultOwner: ownerConfig.isDefaultOwner,
				pageSlugPrefix: ownerConfig.pageSlugPrefix,
			};
			console.log(
				`${sitemapConfig.appTitle} Sitemap: Auto-detected owner filter: ${ownerFilter.ownerId} (isDefault: ${ownerFilter.isDefaultOwner}, prefix: ${ownerFilter.pageSlugPrefix || "none"})`,
			);
		}
	}

	const contentResponses = await Promise.allSettled(
		sitemapConfig?.contentTypes?.map((contentType) =>
			getSitemapContentQuery(contentType, sitemapConfig?.hygraphClient, sitemapConfig?.appTitle, ownerFilter),
		),
	);
	const successfulResponses = contentResponses
		// get successful contentQueries
		?.filter((result): result is PromiseFulfilledResult<HygraphPageContent[]> => result.status === "fulfilled")
		?.map((result) => result?.value)
		?.flat()
		?.filter((el) => !!el);

	return (
		successfulResponses
			// if noIndex is true, skip items
			?.filter(filterOutNoIndexSitemapItems)
			// if slug doesn't start with "/", skip items
			?.filter(filterOutNoSlashSitemapItems)
			// Convert to sitemap items, stripping prefix from slugs
			?.map((content) => convertHygraphItem(content, sitemapConfig, ownerFilter?.pageSlugPrefix))
	);
};

/* Process Additional Slugs */
export const processSitemapSlugs = (sitemapConfig: SitemapConfig): SitemapArray => {
	const localePrefix = sitemapConfig.localePrefix ?? "always";

	return sitemapConfig?.additionalSlugs?.filter(filterSitemapSlug)?.map((slug) => {
		const defaultLocalePath = getLocalizedPath(
			slug,
			sitemapConfig.defaultLocale,
			sitemapConfig.defaultLocale,
			localePrefix,
		);

		return {
			url: `${sitemapConfig?.baseUrl}${defaultLocalePath}`,
			lastModified: new Date(),
			changeFrequency: "daily",
			priority: 0.8,
			alternates: {
				languages: sitemapConfig?.alternates?.reduce((acc: SitemapItemAlternatesLanguages, locale) => {
					const localePath = getLocalizedPath(slug, locale, sitemapConfig.defaultLocale, localePrefix);
					acc[locale] = `${sitemapConfig?.baseUrl}${localePath}`;
					return acc;
				}, {}),
			},
		};
	});
};
